import { isNumber } from 'lodash-es'
import React, { useMemo } from 'react'
import { Navigate, Outlet, useParams } from 'react-router'
import { z } from 'zod'

import AnimatedLoader from '@/atoms/AnimatedLoader/AnimatedLoader'
import { useItinerary } from '@/lib/react-hooks/trip-hooks'
import { routeFor } from '@/lib/route-utils'
import { Routes } from '@/lib/routes'
import { ItineraryFlightBooking, ItineraryRailBooking } from '@/travelsuit'
import { ProductTypeZ, TripIdZ } from '@/types/common'
import { BookingSegmentIdZ } from '@/types/trip'

export const ProductRouteParamsZ = z.object({
	tripId: z.coerce.number().pipe(TripIdZ),
	bookingSegmentId: z.coerce.number().int().positive().pipe(BookingSegmentIdZ).or(ProductTypeZ),
})

export type OutletContext = [
	number,
	z.infer<typeof ProductRouteParamsZ.shape.bookingSegmentId>,
	ItineraryFlightBooking | undefined,
	ItineraryRailBooking | undefined,
	React.ReactNode,
]

export function TripProductBase() {
	const params = useParams()

	const { tripId, bookingSegmentId } = ProductRouteParamsZ.parse(params)
	const itinerary = useItinerary({ tripId }, { refresh: true }, [tripId, bookingSegmentId])
	const flightBookingSegment = isNumber(bookingSegmentId)
		? itinerary?.flights_bookings.find((bs) => bs.id === bookingSegmentId)
		: undefined

	const railBookingSegment = isNumber(bookingSegmentId)
		? itinerary?.rails_bookings.find((bs) => bs.id === bookingSegmentId)
		: undefined

	const itineraryRedirect = useMemo(() => <Navigate to={routeFor(Routes.Itinerary, { id: tripId })} />, [tripId])

	if (isNumber(bookingSegmentId) && !flightBookingSegment && !railBookingSegment) {
		return (
			<AnimatedLoader
				style={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
				}}
			/>
		)
	}
	return <Outlet context={[tripId, bookingSegmentId, flightBookingSegment, railBookingSegment, itineraryRedirect]} />
}
